import { createFileRoute } from '@tanstack/react-router';
import { Box, styled, Text } from '@streamelements/frontend-ui';
import { FormValues } from '../-form/form';
import { Controller, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { OverlayThemesApiClient } from '../../../../api/themes';
import { UnknownError } from '../../../../components/UnknownError';

export const Route = createFileRoute('/_authenticated/onboarding/new-setup/_layout/setup-alerts/')({
  component: SetupAlerts,
  loader: async ({ context: { authContextProvider, serviceEnvironment } }) => {
    const overlayThemesApiClient = new OverlayThemesApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    const themes = await overlayThemesApiClient.getOnboardingThemesOffer();

    return { themes };
  },
  pendingComponent: SetupAlertsPending,
  errorComponent: () => {
    return (
      <UnknownError>
        <Text.Body css={{ mt: '1rem' }}>Failed to fetch available themes.</Text.Body>
      </UnknownError>
    );
  },
});

const OverlayThemeOption = styled(RadioGroup.Item, {
  background: 'none',
  appearance: 'none',
  border: 'none',
  display: 'block',
  width: '100%',
  aspectRatio: '16/9',
  backgroundColor: '$uiQuaternary',
  overflow: 'hidden',
  '&[data-state="checked"]': {
    outline: '4px solid $colors$textPrimaryMain',
    outlineOffset: 0,
  },
});

function SetupAlertsHeader() {
  return (
    <Box css={{ mb: '1rem' }}>
      <Text.Subtitle weight="bold" css={{ m: 0 }}>
        Alerts style
      </Text.Subtitle>
      <Text.Body variant="caption" css={{ color: '$textPrimary50' }}>
        Create your first alert by selecting a style first, you can checkout Elements Gallery for more styles
      </Text.Body>
    </Box>
  );
}

function SetupAlerts() {
  const { themes } = Route.useLoaderData();
  const form = useFormContext<FormValues>();

  useEffect(() => {
    const fieldState = form.getFieldState('overlayTheme');

    if (!fieldState.isDirty) {
      form.setValue('overlayTheme', null);
    }
  }, []);

  return (
    <>
      <SetupAlertsHeader />

      <Controller
        control={form.control}
        name="overlayTheme"
        render={({ field }) => {
          return (
            <RadioGroup.Root
              name="overlayId"
              value={field.value?.themeId ?? 'none'}
              onValueChange={(next) => {
                const theme = themes.find((theme) => theme._id === next)!;
                field.onChange({
                  target: {
                    name: field.name,
                    value: { themeId: theme._id, themeName: theme.title },
                  },
                });
              }}
              asChild
            >
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} aria-label="Alerts style">
                {themes.map((theme) => {
                  return (
                    <OverlayThemeOption
                      key={theme._id}
                      value={theme._id}
                      aria-label={theme.title}
                      style={{
                        backgroundImage: `url(${theme.thumbnail})`,
                        backgroundSize: '100%',
                        backgroundPosition: 'center',
                      }}
                    />
                  );
                })}
              </Box>
            </RadioGroup.Root>
          );
        }}
      />
    </>
  );
}

function SetupAlertsPending() {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <SetupAlertsHeader />

      {Array(5)
        .fill(null)
        .map((_, index) => {
          return (
            <Box
              key={index}
              css={{
                width: '100%',
                aspectRatio: '16/9',
                backgroundColor: '$uiQuaternary',
              }}
            />
          );
        })}
    </Box>
  );
}
